import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { corpusService, CreateWordData } from '../../services/corpusService';
import { CorpusSector } from '../../types';
import { Button } from '../ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

interface AddWordModalProps {
  isOpen: boolean;
  onClose: () => void;
  sectors: CorpusSector[];
  defaultSectorId?: number;
}

export default function AddWordModal({
  isOpen,
  onClose,
  sectors,
  defaultSectorId,
}: AddWordModalProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<CreateWordData>({
    word: '',
    normalizedWord: '',
    mcerlLevel: 'B2',
    sectorIds: defaultSectorId ? [defaultSectorId] : [],
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateWordData) => corpusService.createWord(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['corpus-words'] });
      queryClient.invalidateQueries({ queryKey: ['corpus-stats'] });
      toast.success('Palabra añadida exitosamente');
      resetForm();
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error al añadir palabra');
    },
  });

  const resetForm = () => {
    setFormData({
      word: '',
      normalizedWord: '',
      mcerlLevel: 'B2',
      sectorIds: defaultSectorId ? [defaultSectorId] : [],
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.word.trim() || !formData.normalizedWord.trim()) {
      toast.error('La palabra y su forma normalizada son obligatorias');
      return;
    }
    if (!formData.sectorIds || formData.sectorIds.length === 0) {
      toast.error('Debes seleccionar al menos un sector');
      return;
    }
    createMutation.mutate(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-green-600 text-white p-6 rounded-t-xl flex items-center justify-between">
          <h2 className="text-2xl font-bold">Añadir Palabra</h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Palabra <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.word}
                onChange={(e) => setFormData({ ...formData, word: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Forma Normalizada <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.normalizedWord}
                onChange={(e) => setFormData({ ...formData, normalizedWord: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Nivel MCERL <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.mcerlLevel}
                onChange={(e) =>
                  setFormData({ ...formData, mcerlLevel: e.target.value as any })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                required
              >
                <option value="A1">A1</option>
                <option value="A2">A2</option>
                <option value="B1">B1</option>
                <option value="B2">B2</option>
                <option value="C1">C1</option>
                <option value="C2">C2</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Sílabas</label>
              <input
                type="number"
                value={formData.syllableCount || ''}
                onChange={(e) =>
                  setFormData({ ...formData, syllableCount: e.target.value ? parseInt(e.target.value) : undefined })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Tipo de Palabra
              </label>
              <select
                value={formData.wordType || 'GENERAL'}
                onChange={(e) =>
                  setFormData({ ...formData, wordType: e.target.value as any })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="GENERAL">General</option>
                <option value="TECHNICAL">Técnica</option>
                <option value="SPECIALIZED">Especializada</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Lema</label>
            <input
              type="text"
              value={formData.lemma || ''}
              onChange={(e) => setFormData({ ...formData, lemma: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Sectores <span className="text-red-500">*</span>
            </label>
            <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto">
              {sectors.map((sector) => (
                <label key={sector.id} className="flex items-center gap-2 py-2 hover:bg-gray-50 rounded px-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.sectorIds?.includes(sector.id) ?? false}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({
                          ...formData,
                          sectorIds: [...(formData.sectorIds || []), sector.id]
                        });
                      } else {
                        setFormData({
                          ...formData,
                          sectorIds: (formData.sectorIds || []).filter(id => id !== sector.id)
                        });
                      }
                    }}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <span className="text-2xl mr-1">{sector.icon}</span>
                  <span className="text-sm font-medium text-gray-700">{sector.name}</span>
                </label>
              ))}
            </div>
            {(!formData.sectorIds || formData.sectorIds.length === 0) && (
              <p className="text-xs text-red-500 mt-1">Selecciona al menos un sector</p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" onClick={onClose} variant="outline" className="px-6">
              Cancelar
            </Button>
            <Button
              type="submit"
              className="px-6 bg-green-600 hover:bg-green-700"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Añadiendo...' : 'Añadir Palabra'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
